import {
  Home,
  Compass,
  Search,
  Columns2,
  MessageCircle,
  LayoutDashboard,
  CheckCircle2,
  Zap,
  Building2,
  FileCheck,
  FolderTree,
  Cpu,
  Factory,
  Scale,
  Tags,
  Microscope,
  Network,
  BookOpen,
  Activity,
  BarChart3,
  Terminal,
  Database,
  FileText,
  Settings,
  Stethoscope,
  FlaskConical,
  Pill,
  Scissors,
  MapPin,
  Layers,
  Sparkles,
  CreditCard,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/* ─── 공통 타입 ─── */

export interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /** 이 경로 prefix로 시작하는 페이지도 활성화 */
  activePrefix?: string;
  /** 모바일 탭바에만 표시 */
  mobileOnly?: boolean;
  /** PC 헤더에만 표시 */
  desktopOnly?: boolean;
  /** true이면 Next.js Link 대신 <a> 사용 (Go 서버 페이지 등) */
  external?: boolean;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  /** 기본 열림 상태 (기본값 false) */
  defaultOpen?: boolean;
}

export interface FooterLink {
  href: string;
  label: string;
  /** 굵게 표시 (개인정보처리방침 등) */
  bold?: boolean;
}

/* ─── 메인 네비게이션 (위키/일반 사용자) ─── */

export const mainNavItems: NavItem[] = [
  { href: "/wiki", label: "홈", icon: Home },
  { href: "/wiki/explore", label: "탐색", icon: Compass, activePrefix: "/wiki/explore" },
  { href: "/wiki/search", label: "검색", icon: Search, mobileOnly: true },
  { href: "/wiki/compare", label: "비교", icon: Columns2, activePrefix: "/wiki/compare" },
  { href: "/chat", label: "AI 채팅", icon: MessageCircle },
];

/* ─── Admin 네비게이션 ─── */

export const adminNavGroups: NavGroup[] = [
  {
    title: "",
    defaultOpen: true,
    items: [
      { href: "/admin", label: "대시보드", icon: LayoutDashboard, external: true },
      { href: "/admin/reviews", label: "검토 대기", icon: CheckCircle2, external: true },
      { href: "/admin/process", label: "AI 처리", icon: Zap, external: true },
    ],
  },
  {
    title: "병원 인증",
    defaultOpen: true,
    items: [
      { href: "/admin/claims", label: "인증 신청", icon: Building2 },
      { href: "/admin/changes", label: "변경 신청", icon: FileCheck },
    ],
  },
  {
    title: "데이터 관리",
    items: [
      { href: "/admin/categories", label: "시술 카테고리", icon: FolderTree },
      { href: "/admin/device-names", label: "기기명", icon: Cpu },
      { href: "/admin/device-brands", label: "브랜드 DB", icon: Factory },
      { href: "/admin/kfda", label: "KFDA 의료기기", icon: Pill },
      { href: "/admin/mfds-terms", label: "일상용어", icon: Tags },
      { href: "/admin/law", label: "법령분류", icon: Scale },
      { href: "/admin/cris", label: "CRIS 임상연구", icon: FlaskConical },
      { href: "/admin/mesh", label: "MeSH", icon: Network },
      { href: "/admin/pubmed-matches", label: "PubMed 매칭", icon: BookOpen },
      { href: "/admin/pubmed-keywords", label: "PubMed KW", icon: BookOpen, external: true },
      { href: "/admin/method-categories", label: "방법분류", icon: Scissors },
      { href: "/admin/sungyesa", label: "성예사", icon: Stethoscope },
      { href: "/admin/skinagent-classify", label: "분류관리", icon: Layers },
      { href: "/admin/yeoshin", label: "여신", icon: Microscope },
      { href: "/admin/side-effects", label: "부작용 번역", icon: Activity },
    ],
  },
  {
    title: "병원 데이터",
    items: [
      { href: "/admin/hospitals", label: "병원 목록", icon: MapPin },
      { href: "/admin/procedure-match", label: "시술 매칭", icon: Activity, external: true },
    ],
  },
  {
    title: "시스템",
    items: [
      { href: "/admin/keywords", label: "키워드", icon: Search, external: true },
      { href: "/admin/prompts", label: "프롬프트", icon: FileText, external: true },
      { href: "/admin/search-queries", label: "검색쿼리", icon: Search, external: true },
      { href: "/admin/tables", label: "DB 테이블", icon: Database, external: true },
      { href: "/admin/docs", label: "문서", icon: BookOpen, external: true },
      { href: "/admin/sql", label: "SQL", icon: Database, external: true },
      { href: "/admin/pages", label: "페이지 관리", icon: FileText },
      { href: "/admin/payment", label: "결제 키", icon: CreditCard },
      { href: "/admin/settings", label: "설정", icon: Settings, external: true },
      { href: "/admin/terminal", label: "터미널", icon: Terminal, external: true },
    ],
  },
  {
    title: "수집 작업",
    items: [
      { href: "/admin/jobs", label: "작업 목록", icon: Activity, external: true },
      { href: "/admin/stats", label: "통계", icon: BarChart3, external: true },
      { href: "/admin/data-flow", label: "데이터 플로우", icon: Database },
      { href: "/admin/skills", label: "스킬 카탈로그", icon: Sparkles },
      { href: "/admin/prompts", label: "AI 프롬프트", icon: FileText },
      { href: "/admin/translations", label: "PubMed 번역", icon: FileText },
      { href: "/admin/translations/bench", label: "번역 벤치마크", icon: BarChart3 },
    ],
  },
];

/* ─── 푸터 링크 ─── */

export const footerLinks: FooterLink[] = [
  { href: "/terms", label: "이용약관" },
  { href: "/privacy", label: "개인정보처리방침", bold: true },
  { href: "/youth", label: "청소년보호정책" },
  { href: "/policy", label: "운영정책" },
];

/* ─── 라우트 매칭 ─── */

/**
 * 현재 pathname이 nav item에 매칭되는지 판별.
 * - activePrefix가 있으면 startsWith로 매칭
 * - href가 "/wiki"이면: 다른 nav이 매칭하지 않는 /wiki/* 페이지를 폴백 활성화
 * - 그 외에는 정확 일치
 */
export function matchRoute(pathname: string, item: NavItem): boolean {
  // 정확 일치
  if (pathname === item.href) return true;

  // activePrefix 매칭
  if (item.activePrefix && pathname.startsWith(item.activePrefix + "/")) return true;

  // 위키 홈 폴백: /wiki 하위 경로 중 다른 nav에 매칭 안 되면 홈 활성화
  if (item.href === "/wiki" && pathname.startsWith("/wiki/")) {
    const matchedByOther = mainNavItems.some(
      (other) =>
        other.href !== "/wiki" &&
        other.activePrefix &&
        (pathname === other.href || pathname.startsWith(other.activePrefix + "/")),
    );
    return !matchedByOther;
  }

  return false;
}

/**
 * Admin 네비 아이템 활성 상태 판별.
 * - /admin 대시보드는 정확 일치만
 * - 그 외는 prefix 매칭
 */
export function matchAdminRoute(pathname: string, item: NavItem): boolean {
  if (item.href === "/admin") return pathname === "/admin";
  return pathname === item.href || pathname.startsWith(item.href + "/");
}
